import { useDispatch, useSelector } from 'react-redux';
import { useCrmOrgsApiFacade } from '../../../apis/facades/useCrmOrgsApiFacade';

import {
  setCrmOrgs,
  setCrmOrg,
  deleteCrmOrg,
  addFetchingToOrgId,
  updatePermissionSetGroupAssigned,
  selectDefaultCreationEnvironment,
  updateCrmOrgName,
} from './environmentsReducer';
import { SFDCObjectType } from '../../../types/enums/SFDCObjectType';
import { clearSweepFieldsCache } from '../../../sweep-fields/useCachedSweepFields';
import { setCrmOrgUsers, setCrmOrgUsersBase } from '../../../reducers/crmOrgUsersReducer';
import { useCallback } from 'react';
import { EnvironmentTypes } from './EnvironmentTypeEnum';
import {
  CrmOrgConnectingError,
  getCrmOrgTransientResult,
  OrgAlreadyConnectedRetryData,
  setCrmOrgTransientData,
} from './connectCrmSessionHelper';
import { waitForWindowToClose } from '../helper';
import { ACTIONS_EVENTS, BiEvent } from '../../../services/events';
import { telemetry } from '../../../telemetry';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { retry } from 'ts-retry-promise';
import useUserInfo from '../../../hooks/useUserInfo';
import { useTimeout } from '../../../hooks/useTimeout';
import { AxiosError } from 'axios';
import { CrmOrg } from '../../../types/CrmOrgs';
import {
  CrmOrgCreateDto,
  CrmOrgPlatforms,
  CrmOrgOAuthDto,
  CrmOrgEmailTemplatesResponse,
  ChildRelationshipObjectResponse,
} from '../../../apis/facades/types/crmOrgsApiFacade.types';

interface ConnectErrorResultAbstract {
  result: 'error' | 'success';
  crmOrg?: CrmOrg;
}

interface ConnectErrorResult extends ConnectErrorResultAbstract {
  result: 'error';
  error: CrmOrgConnectingError;
  retryData?: OrgAlreadyConnectedRetryData;
}

interface ConnectSuccessResult extends ConnectErrorResultAbstract {
  result: 'success';
}

type ConnectResult = ConnectErrorResult | ConnectSuccessResult;

const waitForSalesforceOAuthWindowResponse = async (_window: Window, crmOrgId: string) => {
  await waitForWindowToClose(_window);
  const result = getCrmOrgTransientResult(crmOrgId);
  return result;
};

const useCrmOrgs = () => {
  const { setSweepTimeout } = useTimeout();
  const dispatch = useDispatch();
  const {
    get_crmOrgs,
    get_crmOrg,
    delete_org,
    post_crmOrg,
    get_crmOrgUsers,
    get_crmOrgUsersBase,
    patch_crmOrg,
    post_crmOrgSalesforceOauthCode,
    post_connectAnyway,
    post_orgFetch,
    get_crmOrgEmailTemplates,
    get_crmOrgsQueues,
    get_crmOrgsObjectTypesChildRelationshipNames,
    get_crmOrgSalesforceOAuthUrl,
    post_assignPermissionSetGroup,
    get_orgsAll,
  } = useCrmOrgsApiFacade();
  const sendBiEvent = useSendBiEvent();

  const defaultCreationEnv = useSelector(selectDefaultCreationEnvironment);
  const { updateDefaultCreationCrmOrgIdIfNecessary } = useUserInfo();

  const getCrmOrgUsers = useCallback(
    async (includeInactive?: boolean) => {
      if (!defaultCreationEnv) {
        throw new Error('No Default Creation Environment');
      }
      const responseData = await get_crmOrgUsers({
        orgId: defaultCreationEnv.id,
        includeInactive,
      });
      dispatch(
        setCrmOrgUsers({ crmOrgId: defaultCreationEnv.id, crmOrgUsers: responseData.users }),
      );
      return responseData.users;
    },
    [dispatch, get_crmOrgUsers, defaultCreationEnv],
  );

  const getCrmOrgUsersBase = useCallback(
    async (includeInactive?: boolean) => {
      if (!defaultCreationEnv) {
        throw new Error('No Default Creation Environment');
      }
      try {
        const responseData = await get_crmOrgUsersBase({
          orgId: defaultCreationEnv.id,
          includeInactive,
        });
        dispatch(
          setCrmOrgUsersBase({
            crmOrgId: defaultCreationEnv.id,
            crmOrgUsersBase: responseData.users,
          }),
        );
        return responseData.users;
      } catch (err: any) {
        telemetry.captureError(err, {
          message: 'Failed to fetch users.',
        });
        dispatch(setCrmOrgUsersBase({ crmOrgId: defaultCreationEnv.id, crmOrgUsersBase: [] }));
        return [];
      }
    },
    [dispatch, get_crmOrgUsersBase, defaultCreationEnv],
  );

  const getCrmOrgQueues = async (objectType: string) => {
    if (!defaultCreationEnv) {
      throw new Error('No Default Creation Environment');
    }
    const responseData = await get_crmOrgsQueues({ orgId: defaultCreationEnv.id, objectType });

    return responseData.queues;
  };

  const fetchOrg = async (crmOrg: CrmOrg, withReset = false) => {
    const crmOrgId = crmOrg.id;
    dispatch(addFetchingToOrgId({ crmOrgId }));
    clearSweepFieldsCache(setSweepTimeout, 10000);
    try {
      await fetchCrmOrg(crmOrg.id, withReset);
    } catch (err: any) {}
  };

  const createCrmOrg = useCallback(
    async ({ name, platform, isMain, isSandbox }: CrmOrgCreateDto) => {
      const crmOrg = await post_crmOrg({ payload: { name, platform, isMain, isSandbox } });
      dispatch(setCrmOrg({ crmOrg }));
      return crmOrg;
    },
    [dispatch, post_crmOrg],
  );

  const createSalesforceCrmOrg = useCallback(
    async ({ name, isMain, isSandbox }: { name?: string; isMain: boolean; isSandbox: boolean }) =>
      createCrmOrg({
        name: name || (Math.random() + 1).toString(36).substring(7),
        platform: CrmOrgPlatforms.Salesforce,
        isMain,
        isSandbox,
      }),
    [createCrmOrg],
  );

  const renameCrmOrg = useCallback(
    async (orgId: string, name: string) => {
      await patch_crmOrg({ orgId, payload: { name } });
      dispatch(updateCrmOrgName({ orgId, name }));
    },
    [dispatch, patch_crmOrg],
  );

  const setSalesforceOauthCode = useCallback(
    async ({
      crmOrgId,
      authCode,
      isSandbox,
      isOnboardingFastFetch,
      isProcessMiningEnabled,
    }: CrmOrgOAuthDto & { isProcessMiningEnabled?: boolean }) => {
      await post_crmOrgSalesforceOauthCode({
        payload: { crmOrgId, authCode, isSandbox, isOnboardingFastFetch },
        queryParams: { isProcessMiningEnabled: Boolean(isProcessMiningEnabled) },
      });
    },
    [post_crmOrgSalesforceOauthCode],
  );

  const getConnectedCrmOrgs = useCallback(async () => {
    const crmOrgs = await get_crmOrgs({ connectedOnly: true });
    dispatch(setCrmOrgs({ crmOrgs }));
    return crmOrgs;
  }, [dispatch, get_crmOrgs]);

  const getAllConnectedOrgs = useCallback(async () => {
    const crmOrgs = await get_orgsAll({ connectedOnly: true });
    dispatch(setCrmOrgs({ crmOrgs }));
    return crmOrgs;
  }, [dispatch, get_orgsAll]);

  const getCrmOrg = useCallback(
    async (orgId: string) => {
      const crmOrg = await get_crmOrg({ orgId });
      dispatch(setCrmOrg({ crmOrg }));
      return crmOrg;
    },
    [dispatch, get_crmOrg],
  );

  const removeCrmOrg = useCallback(
    async (orgId: string, biEvent?: BiEvent) => {
      await delete_org({ orgId });
      dispatch(deleteCrmOrg({ crmOrgId: orgId }));

      if (biEvent) {
        sendBiEvent(biEvent);
      }
    },
    [delete_org, dispatch, sendBiEvent],
  );

  const getSalesforceRedirectToOAuthUrl = useCallback(
    async (crmOrgId: string) => {
      const { url } = await get_crmOrgSalesforceOAuthUrl({ crmOrgId });
      return url;
    },
    [get_crmOrgSalesforceOAuthUrl],
  );

  const fetchCrmOrg = async (orgId: string, withReset = false) =>
    post_orgFetch({ orgId, withReset });

  const fetchCrmOrgEmailTemplates = async (
    orgId: string,
  ): Promise<CrmOrgEmailTemplatesResponse> => {
    const { emailTemplates } = await get_crmOrgEmailTemplates({ orgId });
    return {
      emailTemplates,
    };
  };

  const fetchChildRelationshipNames = async (
    orgId: string,
    objectType: string,
  ): Promise<ChildRelationshipObjectResponse> => {
    const { childRelationships } = await get_crmOrgsObjectTypesChildRelationshipNames({
      orgId,
      objectType: objectType as SFDCObjectType,
    });

    return {
      childRelationships,
    };
  };

  const awaitOrgConnection = useCallback(
    async (crmOrgId: string) => {
      const getConnectedCrmOrg = async () => {
        try {
          const _crmOrg = await getCrmOrg(crmOrgId);
          if (_crmOrg.isConnected) {
            return _crmOrg;
          } else {
            return null;
          }
        } catch (err) {
          telemetry.captureError(err);
          return null;
        }
      };
      try {
        // Polls until org.isConnected returns true
        const _crmOrg = await retry(getConnectedCrmOrg, {
          until: (t) => t !== null,
          timeout: 20000,
          delay: 2000,
        });

        if (_crmOrg) {
          return _crmOrg;
        }
      } catch (err) {
        telemetry.captureError(err);
      }
    },
    [getCrmOrg],
  );

  const connectOrg = useCallback(
    async ({
      crmOrgToReconnect,
      type,
      forceMain = false,
    }: {
      crmOrgToReconnect?: CrmOrg;
      type: EnvironmentTypes;
      forceMain?: boolean;
    }): Promise<ConnectResult> => {
      let crmOrgToConnect = crmOrgToReconnect;
      const isSandbox = type === EnvironmentTypes.Sandbox;
      if (!crmOrgToConnect) {
        try {
          crmOrgToConnect = await createSalesforceCrmOrg({
            isMain: type === EnvironmentTypes.Production || forceMain,
            isSandbox,
          });
        } catch (err) {
          if (err instanceof AxiosError && err.response?.status === 409) {
            return {
              result: 'error',
              error: CrmOrgConnectingError.ERROR_PROD_ALREADY_CONNECTED,
              crmOrg: undefined,
            };
          }

          return {
            result: 'error',
            error: CrmOrgConnectingError.ERROR_CONNECTING,
            crmOrg: undefined,
          };
        }
      }
      setCrmOrgTransientData({
        crmOrgId: crmOrgToConnect.id,
        isSandbox,
      });
      const sfWindow = window.open(
        await getSalesforceRedirectToOAuthUrl(crmOrgToConnect.id),
        '_blank',
      );
      if (!sfWindow) {
        return {
          result: 'error',
          error: CrmOrgConnectingError.ERROR_CONNECTING,
          crmOrg: crmOrgToConnect,
        };
      }
      const result = await waitForSalesforceOAuthWindowResponse(sfWindow, crmOrgToConnect.id);

      if (!result.success) {
        const errorResult: ConnectErrorResult = {
          result: 'error',
          error: result.error,
          crmOrg: crmOrgToConnect,
        };
        if (
          result.error === CrmOrgConnectingError.ORG_ALREADY_CONNECTED_TO_SWEEP &&
          'retryData' in result
        ) {
          errorResult.retryData = result.retryData;
        }
        return errorResult;
      }

      const crmOrg = await awaitOrgConnection(crmOrgToConnect.id);

      if (crmOrg?.isConnected) {
        updateDefaultCreationCrmOrgIdIfNecessary(crmOrg.id);
        return {
          result: 'success',
          crmOrg,
        };
      } else {
        return {
          result: 'error',
          error: CrmOrgConnectingError.ERROR_CONNECTING,
          crmOrg: crmOrgToConnect,
        };
      }
    },
    [
      getSalesforceRedirectToOAuthUrl,
      awaitOrgConnection,
      createSalesforceCrmOrg,
      updateDefaultCreationCrmOrgIdIfNecessary,
    ],
  );

  const connectAnyway = useCallback(
    async (crmOrg: CrmOrg): Promise<ConnectResult> => {
      try {
        // Complete connection despite "already connected" warning
        // Credentials were stored during the initial OAuth attempt
        await post_connectAnyway(crmOrg.id);

        // Verify the connection was successful and update Redux store
        const updatedCrmOrg = await getCrmOrg(crmOrg.id);

        if (updatedCrmOrg?.isConnected) {
          updateDefaultCreationCrmOrgIdIfNecessary(updatedCrmOrg.id);
          return {
            result: 'success',
            crmOrg: updatedCrmOrg,
          };
        }

        return {
          result: 'error',
          error: CrmOrgConnectingError.ERROR_CONNECTING,
          crmOrg,
        };
      } catch (err) {
        telemetry.captureError(err, {
          message: 'Failed to complete connect-anyway',
          crmOrgId: crmOrg.id,
        });
        return {
          result: 'error',
          error: CrmOrgConnectingError.ERROR_CONNECTING,
          crmOrg,
        };
      }
    },
    [post_connectAnyway, getCrmOrg, updateDefaultCreationCrmOrgIdIfNecessary],
  );

  const assignPermissionSetGroup = useCallback(
    async ({ crmOrgId, assignToAll }: { crmOrgId: string; assignToAll: boolean }) => {
      try {
        await post_assignPermissionSetGroup({ crmOrgId, assignToAll });
        dispatch(updatePermissionSetGroupAssigned({ crmOrgId }));
        sendBiEvent({
          name: ACTIONS_EVENTS.assignSweepPsg,
          props: { type: assignToAll ? 'All' : 'Single' },
        });
      } catch (e) {
        telemetry.captureError(e);
        throw e;
      }
    },
    [post_assignPermissionSetGroup, dispatch, sendBiEvent],
  );

  return {
    getCrmOrg,
    getConnectedCrmOrgs,
    getAllConnectedOrgs,
    setSalesforceOauthCode,
    getSalesforceRedirectToOAuthUrl,
    removeCrmOrg,
    renameCrmOrg,
    fetchOrg,
    fetchCrmOrgEmailTemplates,
    fetchChildRelationshipNames,
    createSalesforceCrmOrg,
    getCrmOrgUsers,
    getCrmOrgUsersBase,
    getCrmOrgQueues,
    connectOrg,
    connectAnyway,
    assignPermissionSetGroup,
  };
};

export { useCrmOrgs };
